import { useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DEFAULT_LOOKUPS,
  INVOICE_STATUS_MAP,
  round2,
  type InvoiceStatus,
  type TenantSettings,
} from '@goyone/shared';
import { api, qs } from '../../lib/api';
import { useTenantProfile } from '../../lib/settings';
import { date, money } from '../../lib/format';
import { Icon } from '../../components/Icon';
import { Badge, ErrorState, Modal, PageHead } from '../../components/ui';
import { Attachments, PendingFiles, uploadAttachments } from '../../components/Attachments';
import { CreditNotice, CreditOverrideDialog, useCredit } from '../../components/Credit';
import { useAuth } from '../../auth/AuthProvider';
import { emptyLine, LineEditor, toPayload, type EditableLine } from '../sales/LineEditor';

interface Invoice {
  id: string;
  number: string;
  status: InvoiceStatus;
  title: string | null;
  notes: string | null;
  terms: string | null;
  issuedAt: string;
  dueAt: string;
  subtotal: string;
  discount: string;
  taxRate: string;
  total: string;
  paidAmount: string;
  account: { id: string; name: string } | null;
  quotation: { id: string; number: string } | null;
  lines: Array<{
    serviceId: string | null;
    description: string;
    quantity: string;
    unit: string;
    unitPrice: string;
    discount: string;
  }>;
  payments: Array<{
    id: string;
    number: string;
    amount: string;
    method: string | null;
    reference: string | null;
    paidAt: string;
  }>;
}

export function InvoiceDetail() {
  const { id = '' } = useParams();
  /* `?from=account` — جاء من ملف العميل فيُمنح زرَّ عودةٍ إليه. */
  const [params] = useSearchParams();
  const isNew = id === 'new';
  const queryClient = useQueryClient();
  const { atLeast } = useAuth();
  const [paying, setPaying] = useState(false);
  const [overriding, setOverriding] = useState(false);

  const invoice = useQuery({
    queryKey: ['invoice', id],
    queryFn: () => api<Invoice>(`/finance/invoices/${id}`),
    enabled: !isNew,
  });

  const send = useMutation({
    mutationFn: (overrideReason?: string) =>
      api(`/finance/invoices/${id}/send`, {
        method: 'POST',
        body: overrideReason ? { overrideReason } : {},
      }),
    onSuccess: () => {
      setOverriding(false);
      refresh();
    },
  });

  /*
   * المبلغ يُمرَّر للمسودة وحدها.
   *
   * الفاتورة المُرسَلة صارت جزءًا من المستحق أصلًا، فتمريرها ثانيةً يحسبها
   * مرتين ويُظهر تجاوزًا لا وجود له.
   */
  const credit = useCredit(
    invoice.data?.account?.id,
    invoice.data?.status === 'DRAFT' ? Number(invoice.data.total) : 0,
  );

  function refresh() {
    void queryClient.invalidateQueries({ queryKey: ['invoice', id] });
    void queryClient.invalidateQueries({ queryKey: ['invoices'] });
    void queryClient.invalidateQueries({ queryKey: ['aging'] });
  }

  if (isNew) return <InvoiceForm />;

  if (invoice.isError) {
    return (
      <div className="page">
        <ErrorState error={invoice.error} />
      </div>
    );
  }

  if (invoice.isPending) {
    return (
      <div className="page">
        <div className="skeleton" style={{ height: 28, width: 220, marginBottom: 20 }} />
        <div className="skeleton" style={{ height: 260 }} />
      </div>
    );
  }

  const doc = invoice.data;
  const statusDef = INVOICE_STATUS_MAP[doc.status];
  const remaining = round2(Number(doc.total) - Number(doc.paidAmount));
  const collectible = remaining > 0 && !['DRAFT', 'CANCELLED'].includes(doc.status);

  const lines: EditableLine[] = doc.lines.map((l) => ({
    serviceId: l.serviceId,
    description: l.description,
    quantity: String(Number(l.quantity)),
    unit: l.unit,
    unitPrice: String(Number(l.unitPrice)),
    discount: String(Number(l.discount)),
  }));

  return (
    <div className="page">
      <PageHead
        title={`فاتورة ${doc.number}`}
        subtitle={doc.title ?? undefined}
        actions={
          <>
            {/* من جاء من ملف العميل يعود إليه — لا إلى قائمةٍ لم يمرّ بها. */}
            {params.get('from') === 'account' && doc.account?.id && (
              <Link to={`/crm/accounts/${doc.account.id}`} className="btn">
                <Icon name="arrowLeft" size={15} />
                ملف العميل
              </Link>
            )}
            <Link to="/finance/invoices" className="btn">
              <Icon name="arrowLeft" size={15} />
              رجوع
            </Link>
            <button
              className="btn"
              onClick={() => window.open(`/print/invoice/${doc.id}`, '_blank')}
            >
              <Icon name="printer" size={15} />
              طباعة
            </button>
            {doc.status === 'DRAFT' && (
              <button
                className={`btn ${credit.data?.blocked ? 'btn-danger' : 'btn-primary'}`}
                onClick={() => {
                  /*
                   * الممنوع يفتح نافذة التخويل لا يصطدم برسالة الخادم.
                   *
                   * ترك المستخدم يضغط ليُردّ عليه بالمنع يجعل الطريق
                   * الصحيح مخفيًا خلف خطأ، وهو ما يدفعه إلى تجريب حيل.
                   */
                  if (credit.data?.blocked) setOverriding(true);
                  else send.mutate(undefined);
                }}
                /*
                 * من لا يملك التخويل يُعطَّل زرّه ولا يُخفى.
                 *
                 * إخفاؤه يترك المندوب لا يفهم لماذا لا يستطيع الإرسال،
                 * وتركه عاملًا يجعله يضغط ليُردّ بالمنع. تعطيله مع نصّه
                 * يقول ما الذي يحتاجه وممن.
                 */
                disabled={
                  send.isPending ||
                  credit.isLoading ||
                  (credit.data?.blocked && !atLeast('ADMIN'))
                }
                title={
                  credit.data?.blocked && !atLeast('ADMIN')
                    ? 'التخويل بتجاوز السقف صلاحية إدارة.'
                    : undefined
                }
              >
                {!credit.data?.blocked
                  ? 'تعليم كمُرسَلة'
                  : atLeast('ADMIN')
                    ? 'تجاوز السقف — يلزم تخويل'
                    : 'تجاوز السقف — يلزم اعتماد الإدارة'}
              </button>
            )}
            {collectible && (
              <button className="btn btn-primary" onClick={() => setPaying(true)}>
                <Icon name="wallet" size={15} />
                تسجيل تحصيل
              </button>
            )}
          </>
        }
      />

      {send.isError && (
        <div className="alert alert-danger mb-4">
          {send.error instanceof Error ? send.error.message : 'تعذّر إتمام العملية.'}
        </div>
      )}

      {doc.status === 'DRAFT' && <CreditNotice credit={credit.data} />}

      {overriding && credit.data && (
        <CreditOverrideDialog
          credit={credit.data}
          pending={send.isPending}
          onClose={() => setOverriding(false)}
          onConfirm={(reason: string) => send.mutate(reason)}
        />
      )}

      <div className="doc-head">
        <div>
          <div className="doc-number">{doc.number}</div>
          <div className="small mute">
            {doc.account ? (
              <Link to={`/crm/accounts/${doc.account.id}`}>{doc.account.name}</Link>
            ) : (
              '—'
            )}
          </div>
        </div>
        <div className="col" style={{ gap: 2 }}>
          <span className="tiny mute">تاريخ الإصدار</span>
          <span>{date(doc.issuedAt)}</span>
        </div>
        <div className="col" style={{ gap: 2 }}>
          <span className="tiny mute">تاريخ الاستحقاق</span>
          <span>{date(doc.dueAt)}</span>
        </div>
        <div className="col" style={{ gap: 2 }}>
          <span className="tiny mute">المتبقي</span>
          <span className="num strong" style={{ color: remaining > 0 ? 'var(--danger)' : undefined }}>
            {money(remaining)}
          </span>
        </div>
        <div style={{ marginInlineStart: 'auto' }}>
          <Badge kind={statusDef?.kind}>{statusDef?.label}</Badge>
        </div>
      </div>

      {doc.quotation && (
        <div className="alert alert-info mb-4">
          <Icon name="clipboard" size={18} />
          <div>
            صادرة عن عرض السعر{' '}
            <Link to={`/sales/quotations/${doc.quotation.id}`}>{doc.quotation.number}</Link>
          </div>
        </div>
      )}

      <div className="card mb-4">
        <div className="card-body">
          <LineEditor
            lines={lines}
            onChange={() => undefined}
            discount={String(Number(doc.discount))}
            onDiscountChange={() => undefined}
            taxRate={String(Number(doc.taxRate))}
            onTaxRateChange={() => undefined}
            disabled
          />
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <h2 className="card-title">التحصيلات</h2>
          <span className="small mute" style={{ marginInlineStart: 'auto' }}>
            محصّل <span className="num">{money(doc.paidAmount)}</span> من{' '}
            <span className="num">{money(doc.total)}</span>
          </span>
        </div>
        <div className="card-body">
          {doc.payments.length === 0 ? (
            <p className="mute small">لم يُسجَّل أي تحصيل بعد.</p>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>الرقم</th>
                    <th>المبلغ</th>
                    <th>الطريقة</th>
                    <th>المرجع</th>
                    <th>التاريخ</th>
                  </tr>
                </thead>
                <tbody>
                  {doc.payments.map((p) => (
                    <tr key={p.id}>
                      <td className="num cell-strong">{p.number}</td>
                      <td className="num">{money(p.amount)}</td>
                      <td className="cell-mute">{p.method ?? '—'}</td>
                      <td className="cell-mute num">{p.reference ?? '—'}</td>
                      <td className="cell-mute">{date(p.paidAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {paying && doc.account && (
        <PaymentModal
          accountId={doc.account.id}
          invoiceId={doc.id}
          invoiceNumber={doc.number}
          canAttach={atLeast('MANAGER')}
          remaining={remaining}
          onClose={() => setPaying(false)}
          onDone={() => {
            setPaying(false);
            refresh();
          }}
        />
      )}
      <div className="mt-4">
        <Attachments entity="invoice" entityId={id} title="مرفقات الفاتورة" />
      </div>

    </div>
  );
}

/* ==========================================================================
   تسجيل تحصيل
   ========================================================================== */

function PaymentModal({
  accountId,
  invoiceId,
  invoiceNumber,
  remaining,
  onClose,
  onDone,
  canAttach,
}: {
  accountId: string;
  invoiceId: string;
  invoiceNumber: string;
  remaining: number;
  onClose: () => void;
  onDone: () => void;
  /** مرفقات «التحصيل» للمدير فما فوق — الأدنى يسجّل الدفعة بلا سند. */
  canAttach: boolean;
}) {
  // المبلغ يبدأ بالمتبقي كاملًا: السداد الكامل هو الحالة الغالبة.
  const [amount, setAmount] = useState(String(remaining));
  const [method, setMethod] = useState(DEFAULT_LOOKUPS.paymentMethod[0]!);
  const [reference, setReference] = useState('');
  const [paidAt, setPaidAt] = useState(new Date().toISOString().slice(0, 10));
  const [files, setFiles] = useState<File[]>([]);
  /* الدفعة حُفظت وتعثّر سندها — لا يُعاد الحفظ فتتكرّر الدفعة. */
  const [uploadError, setUploadError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: async () => {
      const created = await api<{ id: string }>('/finance/payments', {
        method: 'POST',
        body: {
          accountId,
          invoiceId,
          amount: Number(amount),
          method,
          reference: reference || undefined,
          paidAt: new Date(paidAt).toISOString(),
        },
      });
      return files.length ? uploadAttachments('payment', created.id, files) : null;
    },
    onSuccess: (error) => (error ? setUploadError(error) : onDone()),
  });

  const value = Number(amount);
  const valid = value > 0 && value <= remaining + 0.01;

  return (
    <Modal
      title={`تسجيل تحصيل على الفاتورة ${invoiceNumber}`}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} type="button">
            إلغاء
          </button>
          {uploadError ? (
            <button className="btn btn-primary" onClick={onDone}>
              تم
            </button>
          ) : (
            <button
              className="btn btn-primary"
              onClick={() => create.mutate()}
              disabled={!valid || create.isPending}
            >
              {create.isPending ? 'جارٍ الحفظ' : 'حفظ'}
            </button>
          )}
        </>
      }
    >
      {uploadError && (
        <div className="alert alert-warning mb-4">
          سُجّل التحصيل، لكن تعذّر رفع السند: {uploadError} أعد رفعه من «سندات التحصيل» في
          صفحة التحصيلات.
        </div>
      )}
      {create.isError && (
        <div className="alert alert-danger mb-4">
          {create.error instanceof Error ? create.error.message : 'تعذّر الحفظ.'}
        </div>
      )}

      <div className="form-grid">
        <div className="field">
          <label className="label req" htmlFor="p-amount">
            المبلغ
          </label>
          <input
            id="p-amount"
            className="input num"
            type="number"
            min="0"
            step="any"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            autoFocus
          />
          <span className={value > remaining + 0.01 ? 'field-error' : 'tiny mute'}>
            {value > remaining + 0.01
              ? `المبلغ يتجاوز المتبقي وقدره ${money(remaining)}.`
              : `المتبقي على الفاتورة ${money(remaining)}`}
          </span>
        </div>

        <div className="field">
          <label className="label" htmlFor="p-method">
            طريقة الدفع
          </label>
          <select
            id="p-method"
            className="select"
            value={method}
            onChange={(e) => setMethod(e.target.value)}
          >
            {DEFAULT_LOOKUPS.paymentMethod.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label className="label" htmlFor="p-date">
            تاريخ التحصيل
          </label>
          <input
            id="p-date"
            className="input"
            type="date"
            value={paidAt}
            onChange={(e) => setPaidAt(e.target.value)}
          />
        </div>

        <div className="field">
          <label className="label" htmlFor="p-ref">
            المرجع
          </label>
          <input
            id="p-ref"
            className="input"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            placeholder="رقم الشيك أو التحويل"
            dir="ltr"
          />
        </div>

        {canAttach && (
          <PendingFiles
            id="p-files"
            files={files}
            onChange={setFiles}
            label="سند التحصيل (اختياري)"
            hint="إيصال الاستلام أو صورة التحويل أو الشيك — يُرفق على الدفعة."
          />
        )}
      </div>
    </Modal>
  );
}

/* ==========================================================================
   إنشاء فاتورة مباشرة
   ========================================================================== */

/** ينتظر الإعدادات حتى لا تتغير النسبة والشروط تحت يد المستخدم بعد أول رسم. */
function InvoiceForm() {
  const profile = useTenantProfile();

  if (!profile.data) {
    return (
      <div className="page">
        <div className="skeleton" style={{ height: 380 }} />
      </div>
    );
  }

  return <InvoiceFormFields defaults={profile.data.settings} />;
}

function InvoiceFormFields({ defaults }: { defaults: TenantSettings }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [accountId, setAccountId] = useState('');
  const [title, setTitle] = useState('');
  const [dueAt, setDueAt] = useState(
    new Date(Date.now() + defaults.invoiceDueDays * 86_400_000).toISOString().slice(0, 10),
  );
  const [notes, setNotes] = useState('');
  const [terms, setTerms] = useState(defaults.defaultInvoiceTerms);
  const [discount, setDiscount] = useState('0');
  const [taxRate, setTaxRate] = useState(String(defaults.taxRate));
  const [lines, setLines] = useState<EditableLine[]>([emptyLine()]);

  const accounts = useQuery({
    queryKey: ['accounts', 'options'],
    queryFn: () =>
      api<{ items: Array<{ id: string; name: string }> }>(
        `/crm/accounts${qs({ pageSize: 100, sort: 'name', order: 'asc' })}`,
      ),
  });

  const create = useMutation({
    mutationFn: () =>
      api<{ id: string }>('/finance/invoices', {
        method: 'POST',
        body: {
          accountId,
          title: title || undefined,
          notes: notes || undefined,
          terms: terms || undefined,
          dueAt: new Date(dueAt).toISOString(),
          discount: Number(discount) || 0,
          taxRate: Number(taxRate) || 0,
          lines: toPayload(lines),
        },
      }),
    onSuccess: (created) => {
      void queryClient.invalidateQueries({ queryKey: ['invoices'] });
      navigate(`/finance/invoices/${created.id}`);
    },
  });

  const canSave = !!accountId && toPayload(lines).length > 0 && !create.isPending;

  return (
    <div className="page">
      <PageHead
        title="فاتورة جديدة"
        subtitle="تُحفظ كمسودة، ويُعطى رقمها عند الحفظ"
        actions={
          <>
            <Link to="/finance/invoices" className="btn">
              <Icon name="arrowLeft" size={15} />
              رجوع
            </Link>
            <button className="btn btn-primary" onClick={() => create.mutate()} disabled={!canSave}>
              {create.isPending ? 'جارٍ الحفظ' : 'حفظ'}
            </button>
          </>
        }
      />

      {create.isError && (
        <div className="alert alert-danger mb-4">
          {create.error instanceof Error ? create.error.message : 'تعذّر الحفظ.'}
        </div>
      )}

      <div className="card mb-4">
        <div className="card-body">
          <div className="form-grid">
            <div className="field">
              <label className="label req" htmlFor="i-account">
                الحساب
              </label>
              <select
                id="i-account"
                className="select"
                value={accountId}
                onChange={(e) => setAccountId(e.target.value)}
              >
                <option value="">اختر حسابًا</option>
                {accounts.data?.items.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="field">
              <label className="label" htmlFor="i-title">
                عنوان الفاتورة
              </label>
              <input
                id="i-title"
                className="input"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>

            <div className="field">
              <label className="label req" htmlFor="i-due">
                تاريخ الاستحقاق
              </label>
              <input
                id="i-due"
                className="input"
                type="date"
                value={dueAt}
                onChange={(e) => setDueAt(e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <h2 className="card-title">البنود</h2>
        </div>
        <div className="card-body">
          <LineEditor
            lines={lines}
            onChange={setLines}
            discount={discount}
            onDiscountChange={setDiscount}
            taxRate={taxRate}
            onTaxRateChange={setTaxRate}
          />
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <h2 className="card-title">ملاحظات وشروط</h2>
        </div>
        <div className="card-body">
          <div className="form-grid">
            <div className="field span-2">
              <label className="label" htmlFor="i-notes">
                ملاحظات
              </label>
              <textarea
                id="i-notes"
                className="textarea"
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>

            <div className="field span-2">
              <label className="label" htmlFor="i-terms">
                الشروط
              </label>
              <textarea
                id="i-terms"
                className="textarea"
                rows={4}
                value={terms}
                onChange={(e) => setTerms(e.target.value)}
              />
              <span className="tiny mute">
                مأخوذة من إعدادات الشركة، وتقبل التعديل لهذه الفاتورة وحدها.
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
